// Providers e guard de superfície (`spec/17-frontend.md` §4.1 e item 8 da task).
// SÓ WEB-01 edita este arquivo.

import { QueryClientProvider } from '@tanstack/react-query';
import { createRootRoute, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { MotionConfig } from 'motion/react';
import { useEffect } from 'react';
import { registerGateNavigate } from '../app/guards.tsx';
import { ModeProvider } from '../app/mode.tsx';
import { queryClient } from '../app/query-client.ts';
import { ErrorBoundary } from '../app/sentry.ts';
import { StepUpProvider } from '../app/step-up.tsx';
import { isForeignRoute, surface } from '../app/surface.ts';
import { ErrorState } from '../components/app/error-state.tsx';
import { Toaster } from '../components/ui/sonner.tsx';

function NotFound() {
  return (
    <div className="grid min-h-dvh place-items-center p-6 text-center">
      <div className="flex flex-col gap-2">
        <div className="num text-38 text-faint">404</div>
        <p className="text-14 text-muted">Esta página não existe neste painel.</p>
      </div>
    </div>
  );
}

function RootShell() {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  // O `QueryCache` vive fora do React e precisa de um jeito de navegar (`app/gate.ts`).
  useEffect(() => {
    registerGateNavigate((to) => {
      void navigate({ to, replace: true });
    });
    return () => {
      registerGateNavigate(undefined);
    };
  }, [navigate]);

  // A raiz não tem rota: manda para o shell da superfície (o gate leva ao `/login` se
  // não houver sessão). Sem isto `app.` e `admin.` abriam no 404.
  useEffect(() => {
    if (pathname === '/') {
      void navigate({ to: surface() === 'admin' ? '/admin' : '/app', replace: true });
    }
  }, [pathname, navigate]);

  if (pathname === '/') {
    return null;
  }
  if (isForeignRoute(pathname)) {
    return <NotFound />;
  }
  return <Outlet />;
}

function RootComponent() {
  return (
    <ErrorBoundary fallback={({ error }) => <ErrorState error={error} />}>
      {/* `reducedMotion="user"`: com `prefers-reduced-motion` o `motion` zera transform e
          opacity sozinho (`spec/24` §7). A regra de `index.css` CONTINUA, porque ela é
          quem cobre as transições CSS de `components/ui/*` e do `tw-animate-css`, que o
          `MotionConfig` não enxerga. */}
      <MotionConfig reducedMotion="user">
        <QueryClientProvider client={queryClient}>
          <ModeProvider>
            <StepUpProvider>
              <RootShell />
              <Toaster position="top-right" />
            </StepUpProvider>
          </ModeProvider>
        </QueryClientProvider>
      </MotionConfig>
    </ErrorBoundary>
  );
}

export const Route = createRootRoute({
  component: RootComponent,
  notFoundComponent: NotFound,
});
